/**
 * Platform API service for Customer Connects Video API Platform
 */

import { authService } from './authService';

const API_BASE_URL = 'https://dev-platform-api-dev.benjiemalinao879557.workers.dev'; // Always use deployed API

// Demo organization ID (from our deployed database) - fallback only
const DEMO_ORG_ID = 'org_demo_123456789';

export interface Organization {
  id: string;
  name: string;
  email: string;
  plan: string;
  status: string;
  createdAt: string;
  monthlyQuota?: number;
  currentUsage?: number;
}

export interface ApiKey {
  id: string;
  name: string;
  key?: string; // Only returned when created
  keyPrefix: string;
  permissions: string[];
  status: string;
  usageLimit?: number;
  currentUsage: number;
  lastUsedAt?: string;
  createdAt: string;
  expiresAt?: string;
}

export interface CreateApiKeyRequest {
  orgId: string;
  name: string;
  permissions: string[];
  usageLimit?: number;
  expiresIn?: number; // days
}

export interface ApiUsageStats {
  analytics: {
    totalCalls: number;
    totalRooms: number;
    activeRooms: number;
    totalParticipants: number;
    successRate: number;
    avgDuration: number;
  };
  dailyUsage: Array<{
    date: string;
    apiCalls: number;
    roomsCreated: number;
    participants: number;
  }>;
  period: {
    days: number;
    startDate: string;
    endDate: string;
  };
  topEndpoints?: Array<{
    endpoint: string;
    call_count: number;
    avg_response_time: number;
    error_rate: number;
  }>;
  recentActivity?: Array<{
    endpoint: string;
    method: string;
    status_code: number;
    timestamp: string;
    response_time_ms: number;
  }>;
}

export interface SaasAccount {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}

export interface SignInRequest {
  email: string;
  password: string;
}

export interface SignUpRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

export interface UniversalRequest {
  session_id: string;
  request_id: string;
  template_type: 'legal' | 'support' | 'homeimprovement';
  agent_type: string;
  status: 'waiting' | 'accepted' | 'active' | 'completed' | 'expired' | 'cancelled';
  participant_data: {
    name: string;
    email: string;
    phone?: string;
  };
  project_details: {
    title?: string;
    subject?: string;
    case_type?: string;
    category: string;
    urgency?: 'low' | 'medium' | 'high' | 'urgent' | 'planning';
    priority?: 'low' | 'medium' | 'high' | 'urgent';
    description?: string;
    budget?: string;
    address?: string;
  };
  organization_config: {
    company_name: string;
    host_name: string;
  };
  metadata?: {
    source?: string;
    campaign_id?: string;
  };
  assigned_agent_id?: string;
  wait_time_minutes: number;
  created_at: string;
  updated_at: string;
  expires_at: string;
}

export interface AuthResponse {
  success: boolean;
  token: string;
  account: SaasAccount;
}

export class PlatformApiService {
  private baseUrl: string;
  private fallbackOrgId: string;

  constructor(baseUrl: string = API_BASE_URL, fallbackOrgId: string = DEMO_ORG_ID) {
    this.baseUrl = baseUrl;
    this.fallbackOrgId = fallbackOrgId;
  }

  /**
   * Get current organization ID from URL or auth context or fallback
   */
  private getCurrentOrgId(): string {
    // First try to get org ID from URL
    const urlMatch = window.location.pathname.match(/\/(org_[a-zA-Z0-9_]+)\//);
    if (urlMatch && urlMatch[1]) {
      return urlMatch[1];
    }
    
    // Fallback to auth service
    const currentWorkspace = authService.getCurrentWorkspace();
    return currentWorkspace?.organizationId || this.fallbackOrgId;
  }

  /**
   * Make API request with error handling
   */
  private async request<T>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ 
        message: `HTTP ${response.status}: ${response.statusText}` 
      }));
      throw new Error(error.message || `Request failed: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Test API health and connectivity
   */
  async getHealth(): Promise<{ status: string; timestamp: string; version: string }> {
    return this.request('/health');
  }

  /**
   * Test database connection
   */
  async testDatabase(): Promise<{ success: boolean; tables: Array<{ name: string }> }> {
    return this.request('/admin/test-db');
  }

  /**
   * Get API keys for the current organization
   */
  async getApiKeys(): Promise<{ apiKeys: ApiKey[]; orgId: string }> {
    const orgId = this.getCurrentOrgId();
    return this.request(`/admin/api-keys?orgId=${orgId}`);
  }

  /**
   * Create a new API key
   */
  async createApiKey(data: Omit<CreateApiKeyRequest, 'orgId'>): Promise<ApiKey> {
    const orgId = this.getCurrentOrgId();
    const response = await this.request<ApiKey>('/admin/api-keys', {
      method: 'POST',
      body: JSON.stringify({
        ...data,
        orgId: orgId,
      }),
    });
    console.log('platformApi.createApiKey response:', response);
    return response;
  }

  /**
   * Delete/revoke an API key
   */
  async deleteApiKey(keyId: string): Promise<{ success: boolean; message: string }> {
    return this.request(`/admin/api-keys/${keyId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Get usage analytics for the organization
   */
  async getAnalytics(days: number = 30): Promise<ApiUsageStats> {
    const orgId = this.getCurrentOrgId();
    return this.request(`/admin/analytics?orgId=${orgId}&days=${days}`);
  }

  /**
   * Get organization details
   */
  async getOrganization(): Promise<Organization> {
    const orgId = this.getCurrentOrgId();
    // For now, return demo org data
    // In the future, this would come from an API endpoint
    return {
      id: orgId,
      name: 'Demo Organization',
      email: 'demo@customerconnects.app',
      plan: 'pro',
      status: 'active',
      createdAt: '2024-01-01T00:00:00.000Z',
      monthlyQuota: 10000,
      currentUsage: 0,
    };
  }

  /**
   * Sign in to the SaaS platform
   */
  async signIn(credentials: SignInRequest): Promise<AuthResponse> {
    return this.request('/auth/signin', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  /**
   * Sign up for the SaaS platform
   */
  async signUp(userData: SignUpRequest): Promise<{ success: boolean; message: string; accountId: string }> {
    return this.request('/auth/signup', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  /**
   * Save onboarding data for organization (stores in dev_platform_organizations.settings JSON)
   */
  async saveOnboarding(orgId: string, data: {
    agencyName: string;
    crm: string;
    useCases: string[];
    otherNotes?: string;
    completedAt: string;
  }): Promise<{ success: boolean; message: string }>{
    return this.request(`/admin/onboarding?orgId=${orgId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Sign out from the platform
   */
  async signOut(token: string): Promise<{ success: boolean; message: string }> {
    return this.request('/auth/signout', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  /**
   * Format API key for display (mask sensitive parts)
   */
  static formatApiKey(key: string): string {
    if (key.length <= 16) return key;
    return key.substring(0, 12) + '•'.repeat(16) + key.substring(key.length - 4);
  }

  /**
   * Parse permissions from JSON string (if needed)
   */
  static parsePermissions(permissions: string | string[]): string[] {
    if (Array.isArray(permissions)) return permissions;
    try {
      return JSON.parse(permissions);
    } catch {
      return [];
    }
  }

  /**
   * Get available permissions list
   */
  static getAvailablePermissions() {
    return [
      { id: 'video:create', label: 'Create Video Rooms', description: 'Create new video conference rooms' },
      { id: 'video:manage', label: 'Manage Video Rooms', description: 'Update, delete, and manage existing rooms' },
      { id: 'video:join', label: 'Generate Join Tokens', description: 'Create tokens for participants to join rooms' },
      { id: 'analytics:read', label: 'Read Analytics', description: 'Access usage analytics and metrics' },
      { id: 'webhooks:manage', label: 'Manage Webhooks', description: 'Create and manage webhook endpoints' },
    ];
  }

  /**
   * Get usage summary for billing (admin access - no API key required)
   */
  async getUsageSummary(period: string = 'current_month'): Promise<any> {
    const orgId = this.getCurrentOrgId();
    return this.request(`/admin/usage-summary?orgId=${orgId}&period=${period}`);
  }

  /**
   * Get quotas for billing (admin access - no API key required)
   */
  async getQuotas(): Promise<any> {
    const orgId = this.getCurrentOrgId();
    return this.request(`/admin/quotas?orgId=${orgId}`);
  }

  /**
   * Get billing events (admin access - no API key required)
   */
  async getBillingEvents(limit: number = 10): Promise<any> {
    const orgId = this.getCurrentOrgId();
    return this.request(`/admin/billing-events?orgId=${orgId}&limit=${limit}`);
  }

  /**
   * Get webhooks for the organization (admin access - no API key required)
   */
  async getWebhooks(): Promise<any> {
    const orgId = this.getCurrentOrgId();
    return this.request(`/admin/webhooks?orgId=${orgId}`);
  }

  /**
   * Create a new webhook (admin access - no API key required)
   */
  async createWebhook(data: {
    name: string;
    url: string;
    events: string[];
    retryCount?: number;
    timeoutMs?: number;
  }): Promise<any> {
    const orgId = this.getCurrentOrgId();
    return this.request(`/admin/webhooks?orgId=${orgId}`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * Update a webhook (admin access - no API key required)
   */
  async updateWebhook(webhookId: string, data: any): Promise<any> {
    const orgId = this.getCurrentOrgId();
    return this.request(`/admin/webhooks/${webhookId}?orgId=${orgId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  /**
   * Delete a webhook (admin access - no API key required)
   */
  async deleteWebhook(webhookId: string): Promise<any> {
    const orgId = this.getCurrentOrgId();
    return this.request(`/admin/webhooks/${webhookId}?orgId=${orgId}`, {
      method: 'DELETE'
    });
  }

  /**
   * Get universal requests for dashboard (session-based auth)
   */
  async getUniversalRequests(params?: {
    status?: string;
    templateType?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ success: boolean; requests: UniversalRequest[]; total: number; limit: number; offset: number }> {
    const orgId = this.getCurrentOrgId();
    const queryParams = new URLSearchParams({ orgId });
    
    if (params?.status) queryParams.set('status', params.status);
    if (params?.templateType) queryParams.set('template_type', params.templateType);
    if (params?.limit) queryParams.set('limit', params.limit.toString());
    if (params?.offset) queryParams.set('offset', params.offset.toString());

    return this.request(`/dashboard/universal/requests?${queryParams.toString()}`);
  }

  /**
   * Accept universal request for dashboard (session-based auth)
   */
  async acceptUniversalRequest(sessionId: string, agentData: {
    agent_id: string;
    agent_name: string;
  }): Promise<{ success: boolean; session_id: string; room_id?: string; video_url?: string; status: string; participant_data: any }> {
    const orgId = this.getCurrentOrgId();
    return this.request(`/dashboard/universal/accept/${sessionId}?orgId=${orgId}`, {
      method: 'POST',
      body: JSON.stringify(agentData)
    });
  }

  /**
   * Reject universal request for dashboard (session-based auth)
   */
  async rejectUniversalRequest(sessionId: string, agentData: {
    agent_id: string;
    agent_name: string;
    rejection_reason?: string;
  }): Promise<{ success: boolean; session_id: string; status: string; message?: string }> {
    const orgId = this.getCurrentOrgId();
    return this.request(`/dashboard/universal/reject/${sessionId}?orgId=${orgId}`, {
      method: 'POST',
      body: JSON.stringify(agentData)
    });
  }
}

// Export singleton instance
export const platformApi = new PlatformApiService();
export default platformApi;